import { useMemo, useRef, useState } from 'react';
import { parseQuickAdd } from '../lib/parseQuickAdd';

/**
 * Quick-add cluster extracted from EventListModule (#3277).
 *
 * Owns the lightweight create-event form state (`quickAddText`,
 * `quickAddDate`, `quickAddCalendarId`, `newEventInitialDate`), the input ref,
 * the rotating placeholder data + `getPlaceholder`, and the `handleQuickAdd`
 * orchestration (parse → date/time resolution → create payload → navigate →
 * reset → background-sync).
 *
 * Extraction notes (byte-for-byte behavior preservation):
 * - `handleQuickAdd` was a NON-memoized plain function in the orchestrator,
 *   invoked only on user action; it is reproduced here verbatim, reading the
 *   live deps passed in `opts` at call time (no useCallback, so no dependency
 *   array to preserve and no memoization-identity change).
 * - The hook is called ONCE at a stable position AFTER the Google-sync hook and
 *   the create/update mutations are declared, so `createEvent`,
 *   `allWritableCalendars`, `setCalendarDate`, `calendarConnected`, and
 *   `debouncedBackgroundSync` are available — matching the original lexical
 *   capture (those consts were declared later than the inline closure but were
 *   only ever read at call time, never during render).
 * - The placeholder arrays + the `placeholdersWithDay` useMemo (empty deps) +
 *   the `placeholderIdxRef` (seeded with a one-time random index) are moved
 *   unchanged, preserving the rotate-on-each-call cadence.
 */
export interface UseQuickAddOptions {
  moduleId: string;
  calendarDate: Date;
  setCalendarDate: (d: Date) => void;
  allWritableCalendars: Array<{ id: string; primary?: boolean }>;
  createEvent: { mutate: (data: Record<string, unknown>) => void };
  calendarConnected: boolean;
  debouncedBackgroundSync: () => void;
}

export function useQuickAdd(opts: UseQuickAddOptions) {
  const {
    moduleId,
    calendarDate,
    setCalendarDate,
    allWritableCalendars,
    createEvent,
    calendarConnected,
    debouncedBackgroundSync,
  } = opts;

  const [newEventInitialDate, setNewEventInitialDate] = useState<Date | null>(null);
  const [quickAddText, setQuickAddText] = useState('');
  const [quickAddDate, setQuickAddDate] = useState<Date | null>(null);
  const [quickAddCalendarId, setQuickAddCalendarId] = useState(() =>
    localStorage.getItem(`xavior:last-calendar:${moduleId}`) || ''
  );
  const quickAddRef = useRef<HTMLInputElement>(null);

  // Generate placeholders with real upcoming dates
  const placeholderTemplatesWithDay = [
    ['dinner with George Clooney', '7pm', 'Giorgio Baldi'],
    ['Oscars after-party', '10pm', 'Chateau Marmont'],
    ['yacht brunch with Oprah', '11am', 'Marina del Rey'],
    ['tennis with Roger Federer', '8am', ''],
    ['shopping with Rihanna', '2pm', 'Rodeo Drive'],
    ['coffee with Elon', '9am', 'SpaceX HQ'],
    ['gallery opening with Jay-Z', '6pm', 'Gagosian'],
    ['sushi with Beyoncé', '8pm', 'Nobu Malibu'],
    ['helicopter to Coachella', '10am', ''],
    ['fitting at Tom Ford', '3pm', ''],
    ['accept Pulitzer Prize', '11am', ''],
    ['vinyl session with Pharrell', '4pm', ''],
    ['sunrise hike with The Rock', '5:30am', 'Runyon Canyon'],
    ['poker night with Ben Affleck', '9pm', ''],
    ['book club with Reese Witherspoon', '10am', ''],
  ];
  const placeholdersWithDay = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return placeholderTemplatesWithDay.map(([desc, time, place], i) => {
      const d = new Date(); d.setDate(d.getDate() + i + 2);
      const dateStr = `${months[d.getMonth()]} ${d.getDate()}`;
      return `${desc} ${dateStr} ${time}${place ? ' ' + place : ''}`;
    });
    // Empty deps: placeholders are seeded once at mount (matching the original
    // inline memo). The repo's eslint config does not register
    // react-hooks/exhaustive-deps, so no disable directive is needed.
  }, []);
  const placeholdersNoDay = [
    'dinner with George Clooney 7pm Giorgio Baldi',
    'Oscars after-party 10pm Chateau Marmont',
    'yacht brunch with Oprah 11am',
    'tennis with Roger Federer 8am',
    'shopping with Rihanna 2pm Rodeo Drive',
    'coffee with Elon 9am SpaceX HQ',
    'gallery opening with Jay-Z 6pm Gagosian',
    'sushi with Beyoncé 8pm Nobu Malibu',
    'helicopter to Coachella 10am',
    'fitting at Tom Ford 3pm',
    'accept Pulitzer Prize 11am',
    'vinyl session with Pharrell 4pm',
    'sunrise hike with The Rock 5:30am',
    'poker night with Ben Affleck 9pm',
    'book club with Reese Witherspoon 10am',
  ];
  const placeholderIdxRef = useRef(Math.floor(Math.random() * 15));
  const getPlaceholder = (hasDate: boolean) => {
    const list = hasDate ? placeholdersNoDay : placeholdersWithDay;
    placeholderIdxRef.current = (placeholderIdxRef.current + 1) % list.length;
    return list[placeholderIdxRef.current];
  };

  /**
   * Returns `true` when an event was created (the path that, in the original
   * inline closure, fell through to `setIsCreating(false)`); returns `false`
   * on the empty-text early-return so the orchestrator can preserve the
   * original behavior of NOT closing the modal in that case.
   */
  const handleQuickAdd = (): boolean => {
    const text = quickAddText.trim();
    if (!text) return false;
    const parsed = parseQuickAdd(text, calendarDate);
    let startAt = parsed.startAt;
    const userTypedTime = text.match(/\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b/i) || text.match(/\b\d{1,2}:\d{2}\b/);
    const userTypedDate = text.match(/\btomorrow\b/i) || text.match(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\b/i) || text.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d/i) || text.match(/\b\d{1,2}\/\d{1,2}\b/);
    if (newEventInitialDate) {
      if (!userTypedDate) {
        // Keep the clicked date, but use parsed time if user typed one
        startAt = new Date(newEventInitialDate);
        if (userTypedTime) {
          startAt.setHours(parsed.startAt.getHours(), parsed.startAt.getMinutes(), 0, 0);
        }
      }
      // If user typed both date and time, use fully parsed result (startAt stays as parsed)
    }
    const endAt = new Date(startAt);
    endAt.setHours(endAt.getHours() + 1);
    const eventData: Record<string, any> = {
      title: parsed.title,
      startAt: startAt.toISOString(),
      endAt: endAt.toISOString(),
      moduleLinks: [{ moduleId }],
    };
    const targetCalId = quickAddCalendarId || allWritableCalendars.find(c => c.primary)?.id || allWritableCalendars[0]?.id;
    if (targetCalId) eventData.providerCalendarId = targetCalId;
    createEvent.mutate(eventData);
    // Navigate calendar to the event's date
    setCalendarDate(startAt);
    setQuickAddText('');
    setQuickAddDate(null);
    setNewEventInitialDate(null);
    // setIsCreating(false) stays in the orchestrator (modal-visibility state);
    // signalled via the `true` return below.
    if (calendarConnected) debouncedBackgroundSync();
    return true;
  };

  return {
    newEventInitialDate,
    setNewEventInitialDate,
    quickAddText,
    setQuickAddText,
    quickAddDate,
    setQuickAddDate,
    quickAddCalendarId,
    setQuickAddCalendarId,
    quickAddRef,
    getPlaceholder,
    handleQuickAdd,
  };
}
